import React, { useCallback, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { AlertTriangle, BadgeCheck, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { useSocialPost } from '@contexts/SocialPostContext';
import { useSelectedSocialAccount } from '@hooks/useSelectedSocialAccount';
import socialService from '@services/socialService';
import type { SocialPost } from '@/types/social';
import { formatDateTimeBR } from '@/utils/formatters';
import { effectiveCaption, formatPostTime } from '@/utils/social';
import { PillarDot, PostCover, PostNetworks, StatusChip } from '@components/social/SocialBits';
import { ProfileSwitcher } from '@components/social/ProfileSwitcher';

/**
 * Aprovações: fila "Aguardando aprovação" com aprovar (selo verde) e pedir ajustes com
 * comentário direto no cartão; abaixo, os posts com ajustes pedidos e os aprovados recentes.
 * Quem aprova: admin ou gestor (nunca o próprio post). Documentação: src/docs/redes-sociais/aprovacoes.md.
 */
export const SocialApprovalsView: React.FC = () => {
  const [awaiting, setAwaiting] = useState<SocialPost[]>([]);
  const [changes, setChanges] = useState<SocialPost[]>([]);
  const [approved, setApproved] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [error, setError] = useState<unknown>(null);
  const { openPost, version } = useSocialPost();
  const { accounts, accountId, setAccountId, ready } = useSelectedSocialAccount();

  const load = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    const account_id = accountId ?? undefined;
    try {
      const [a, c, ok] = await Promise.all([
        socialService.listPosts({ status: ['awaiting_approval'], account_id }),
        socialService.listPosts({ status: ['changes_requested'], account_id }),
        socialService.listPosts({ status: ['approved', 'scheduled'], account_id }),
      ]);
      setAwaiting(a.sort((x, y) => (x.scheduled_at ?? '9').localeCompare(y.scheduled_at ?? '9')));
      setChanges(c);
      setApproved(ok.slice(0, 12));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [accountId, ready]);

  useEffect(() => {
    load();
  }, [load, version]);

  const act = async (p: SocialPost, kind: 'approve' | 'changes') => {
    const note = (notes[p.id] ?? '').trim();
    if (kind === 'changes' && !note) {
      setError(new Error('Escreva no comentário o que precisa ser ajustado.'));
      return;
    }
    setBusy(p.id);
    try {
      if (kind === 'approve') await socialService.approve(p.id, note);
      else await socialService.requestChanges(p.id, note);
      setNotes((n) => ({ ...n, [p.id]: '' }));
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const card = (p: SocialPost, actions: boolean) => {
    const errors = p.issues.filter((i) => i.level === 'error');
    return (
      <div key={p.id} className="card p-3 flex flex-col sm:flex-row gap-3">
        <button type="button" onClick={() => openPost(p.id)} className="flex-shrink-0">
          <PostCover post={p} className="w-full sm:w-40 aspect-[4/5] rounded-lg" />
        </button>
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => openPost(p.id)} className="font-semibold text-slate-800 hover:underline text-left">
              {p.title}
            </button>
            <StatusChip status={p.status} />
            <PostNetworks post={p} />
          </div>
          <p className="text-xs text-slate-500">
            {formatPostTime(p.scheduled_at) || 'sem data'} · {p.owner_name ?? p.created_by}
            {p.submitted_at && ` · enviado em ${formatDateTimeBR(p.submitted_at)}`}
          </p>
          <PillarDot color={p.pillar_color} name={p.pillar_name} />
          <p className="text-sm text-slate-700 whitespace-pre-wrap line-clamp-4">{effectiveCaption(p.caption, p.targets[0]?.caption)}</p>
          {errors.length > 0 && (
            <p className="text-xs text-red-700 flex gap-1">
              <AlertTriangle size={12} className="mt-0.5" /> {errors.map((e) => e.message).join(' · ')}
            </p>
          )}
          {actions && p.can_approve && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <input
                className="input-field flex-1 min-w-[200px] text-sm"
                placeholder="Comentário (obrigatório para pedir ajustes)"
                value={notes[p.id] ?? ''}
                onChange={(e) => setNotes((n) => ({ ...n, [p.id]: e.target.value }))}
              />
              <Button size="sm" variant="outline" icon={<RotateCcw />} loading={busy === p.id} onClick={() => act(p, 'changes')}>
                Pedir ajustes
              </Button>
              <Button size="sm" variant="success" icon={<BadgeCheck />} loading={busy === p.id} onClick={() => act(p, 'approve')}>
                Aprovar
              </Button>
            </div>
          )}
          {actions && !p.can_approve && (
            <p className="text-[11px] text-slate-400">Aguardando o gestor — você não aprova este post (é seu ou você não é gestor).</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <ProfileSwitcher accounts={accounts} value={accountId} onChange={setAccountId} allowAll />
      <section className="space-y-3">
        <h2 className="text-h4 flex items-center gap-2">
          Aguardando aprovação <span className={clsx('badge-warning')}>{awaiting.length}</span>
          {loading && <Loader2 size={16} className="animate-spin text-slate-400" />}
        </h2>
        {awaiting.length === 0 && !loading && <p className="text-sm text-slate-500">Nada esperando aprovação. 🎉</p>}
        {awaiting.map((p) => card(p, true))}
      </section>
      <section className="space-y-3">
        <h2 className="text-h4">Ajustes solicitados ({changes.length})</h2>
        {changes.map((p) => card(p, false))}
      </section>
      <section className="space-y-3">
        <h2 className="text-h4">Aprovados e agendados</h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
          {approved.map((p) => (
            <button key={p.id} type="button" onClick={() => openPost(p.id)} className="text-left">
              <PostCover post={p} className="aspect-[4/5] rounded-lg" />
              <p className="mt-1 truncate text-xs text-slate-700">{p.title}</p>
              <p className="text-[11px] text-slate-500">{formatPostTime(p.scheduled_at) || 'sem data'}</p>
            </button>
          ))}
        </div>
      </section>
      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};

export default SocialApprovalsView;
